import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Copy, CheckCircle, Loader2, Sparkles, CreditCard, History, Trash2, Eye, Calendar, FileText, Settings, CreditCard as CreditCardIcon, Calendar as CalendarIcon, ChevronDown, LogOut } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import { AuthProvider, useAuth } from './context/Authcontext.jsx';
import LoginForm from './components/auth/LoginForm';
import SignupForm from './components/auth/SignupForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import VerifyEmailPrompt from './components/auth/VerifyEmailPrompt';
import DOMPurify from 'dompurify';

const API_URL = 'https://ai-content-creator-backend-production.up.railway.app/api';

function ContentCreatorApp() {
  const [currentView, setCurrentView] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyFormat, setCopyFormat] = useState('markdown'); // 'plain', 'html', or 'markdown'
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const dropdownRef = useRef(null);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: ''
  });
  

  // Email verification state
  const [showEmailVerificationBanner, setShowEmailVerificationBanner] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const [contentType, setContentType] = useState('');
  const [topic, setTopic] = useState('');
  const [topicCharCount, setTopicCharCount] = useState(0);
  const [keywords, setKeywords] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [error, setError] = useState('');

  // Content History State
  const [contentHistory, setContentHistory] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyStats, setHistoryStats] = useState(null);

  // Checks email verification on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verificationToken = urlParams.get('token');
    
    if (verificationToken && window.location.pathname === '/verify-email') {
      verifyEmail(verificationToken);
    }
  }, []);

  // Shows banner if not verified
  useEffect(() => {
    if (user && !user.emailVerified && currentView === 'dashboard') {
      setShowEmailVerificationBanner(true);
    } else {
      setShowEmailVerificationBanner(false);
    }
  }, [user, currentView]);

useEffect(() => {
  const savedToken = localStorage.getItem('token');
  const savedUser = localStorage.getItem('user');
  
  // Check URL parameters once
  const urlParams = new URLSearchParams(window.location.search);
  const upgradeParam = urlParams.get('upgrade');
  const stripeSuccess = urlParams.get('success');
  
  if (savedToken && savedUser) {
    const parsedUser = JSON.parse(savedUser);
    setToken(savedToken);
    setUser(parsedUser);
    
    // Set subscription status immediately from saved user data
    if (parsedUser.subscription) {
      const calculateTrialDays = (trialEndDate) => {
        if (!trialEndDate) return 0;
        const end = new Date(trialEndDate);
        const now = new Date();
        const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return Math.max(0, daysLeft);
      };
      
      const subStatus = {
        status: parsedUser.subscription.status,
        isTrialActive: parsedUser.subscription.isTrialActive,
        trialDaysRemaining: calculateTrialDays(parsedUser.subscription.trialEndDate),
        trialEndDate: parsedUser.subscription.trialEndDate,
        stripeCurrentPeriodEnd: parsedUser.subscription.stripeCurrentPeriodEnd,
        hasActiveSubscription: parsedUser.subscription.status === 'active'
      };
      setSubscriptionStatus(subStatus);
      setIsLoadingSubscription(false);
    }
    
    // Check if user is admin
    checkAdminStatus(savedToken).then(isAdminUser => {
      setIsAdmin(isAdminUser);
      if (isAdminUser) {
        setCurrentView('admin');
      } else if (upgradeParam === 'true') {
        // Auto-open subscription view if upgrade param present
        setCurrentView('upgrade');
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setCurrentView('dashboard');
      }
    });
  }

  // Check for successful Stripe checkout
  if (stripeSuccess === 'true') {
    setError('');
    setTimeout(() => {
      if (savedToken) {
        fetchSubscriptionStatus(savedToken);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }, 2000);
  }
}, []);

  // Check for password reset token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const path = window.location.pathname;
    
    if (token && path === '/reset-password') {
      setResetToken(token);
      setCurrentView('reset-password');
      // Clear URL
      window.history.replaceState({}, document.title, '/reset-password');
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    // Email Verification Banner Component
    const EmailVerificationBanner = () => (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertCircle className="text-yellow-400 mr-3" size={24} />
          <div>
            <p className="text-yellow-800 font-medium">Please verify your email address</p>
            <p className="text-yellow-700 text-sm">
              Check your inbox for a verification link. Didn't receive it?
            </p>
          </div>
        </div>
        <button
          onClick={resendVerificationEmail}
          disabled={isLoading}
          className="ml-4 px-4 py-2 bg-yellow-400 text-yellow-900 rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 text-sm font-medium"
        >
          {isLoading ? 'Sending...' : 'Resend Email'}
        </button>
      </div>
    </div>
  );  

  // Fetch content history when filter changes
  useEffect(() => {
    if (currentView === 'history' && token) {
      fetchContentHistory();
    }
  }, [historyFilter, currentView]);

  const fetchSubscriptionStatus = async () => {
  setIsLoadingSubscription(true);
  try {
    const response = await fetch(`${API_URL}/subscription/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      setSubscriptionStatus(data);
    }
  } catch (error) {
    console.error('Failed to fetch subscription status:', error);
  } finally {
    setIsLoadingSubscription(false);
  }
};

// Close format menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFormatMenu && !event.target.closest('.copy-format-container')) {
        setShowFormatMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFormatMenu]);

  // Verification function
 const verifyEmail = async (token) => {
  setIsLoading(true);
  try {
    const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`);
    const data = await response.json();

    if (response.ok) {
      setVerificationMessage('Email verified successfully! ');
      
      // NEW: If user is logged in, fetch fresh user data from backend
      const authToken = localStorage.getItem('token');
      if (authToken) {
        await fetchUser(authToken); // This will update the user state with verified status
        setCurrentView('dashboard');
      } else {
        setCurrentView('login');
      }
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Clear success message after 5 seconds
      setTimeout(() => setVerificationMessage(''), 5000);
    } else {
      setError(data.error || 'Email verification failed');
      setCurrentView('login');
    }
  } catch (err) {
    setError('Failed to verify email');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

// Fetch user data from backend
const fetchUser = async (authToken) => {
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      const userData = data.user || data; // Handle wrapped response
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    }
  } catch (err) {
    console.error('Failed to fetch user:', err);
  }
};

const resendVerificationEmail = async () => {
  if (!user || !user.email) return;
  
  setIsLoading(true);
  setError('');
  
  try {
    const response = await fetch(`${API_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: user.email }) // Send email in body
    });

    const data = await response.json();

    if (response.ok) {
      setVerificationMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setVerificationMessage(''), 5000);
    } else {
      setError(data.message || 'Failed to resend verification email');
    }
  } catch (err) {
    setError('Failed to resend verification email');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

  const checkAdminStatus = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const fetchContentHistory = async () => {
    setIsLoading(true);
    try {
      const url = historyFilter === 'all' 
        ? `${API_URL}/content/history`
        : `${API_URL}/content/history?contentType=${historyFilter}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setContentHistory(data.content || []);
      } else {
        setError(data.error || 'Failed to fetch content history');
      }
    } catch (err) {
      setError('Failed to fetch content history');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContentStats = async () => {
    try {
      const response = await fetch(`${API_URL}/content/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setHistoryStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const viewContentDetail = async (contentId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/content/${contentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setSelectedContent(data.content);
        setCurrentView('content-detail');
      } else {
        setError(data.error || 'Failed to load content');
      }
    } catch (err) {
      setError('Failed to load content');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/content/${contentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh the list
        fetchContentHistory();
        setError('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete content');
      }
    } catch (err) {
      setError('Failed to delete content');
      console.error(err);
    }
  };

  const handleCreateCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to create checkout session');
        setIsLoading(false);
        return;
      }

      window.location.href = data.url;

    } catch (err) {
      setError('Failed to connect to payment service');
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/subscription/create-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to open billing portal');
        setIsLoading(false);
        return;
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;

    } catch (err) {
      setError('Failed to connect to billing service');
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    setError('');
    
    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (signupData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!signupData.email || !signupData.firstName || !signupData.lastName) {
      setError('All fields are required');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signupData.email,
          password: signupData.password,
          firstName: signupData.firstName,
          lastName: signupData.lastName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Signup failed');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      setCurrentView('dashboard');
      fetchSubscriptionStatus(data.token);
      
    } catch (err) {
      setError('Failed to connect to server. Make sure backend is running.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
  setError('');
  
  if (!loginData.email || !loginData.password) {
    setError('Please enter email and password');
    return;
  }
  setIsLoading(true);
  
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: loginData.email,
        password: loginData.password
      })
    });
    const data = await response.json();
    if (!response.ok) {
      setError(data.error || 'Login failed');
      setIsLoading(false);
      return;
    }
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    
    // Set subscription status immediately from login response as fallback
    if (data.user.subscription) {
      const calculateTrialDays = (trialEndDate) => {
        if (!trialEndDate) return 0;
        const end = new Date(trialEndDate);
        const now = new Date();
        const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return Math.max(0, daysLeft);
      };
      
      const subStatus = {
        status: data.user.subscription.status,
        isTrialActive: data.user.subscription.isTrialActive,
        trialDaysRemaining: calculateTrialDays(data.user.subscription.trialEndDate),
        trialEndDate: data.user.subscription.trialEndDate,
        stripeCurrentPeriodEnd: data.user.subscription.stripeCurrentPeriodEnd,
        hasActiveSubscription: data.user.subscription.status === 'active'
      };
      setSubscriptionStatus(subStatus);
    }
    
    await fetchSubscriptionStatus(data.token);  // Fetch fresh status, overrides if successful
    
    setCurrentView('dashboard');
    
  } catch (err) {
    setError('Failed to connect to server. Make sure backend is running.');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

  // Request password reset
const handleRequestPasswordReset = async () => {
  setError('');
  setResetMessage('');
  
  if (!resetEmail) {
    setError('Please enter your email address');
    return;
  }

  setIsLoading(true);
  
  try {
    const response = await fetch(`${API_URL}/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: resetEmail })
    });

    const data = await response.json();

    if (response.ok) {
      setResetMessage(data.message);
      setResetEmail('');
    } else {
      setError(data.error || 'Failed to send reset email');
    }
  } catch (err) {
    setError('Failed to connect to server');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

// Reset password with token
const handleResetPassword = async () => {
  setError('');
  setResetMessage('');
  
  if (!newPassword || !confirmNewPassword) {
    setError('Please fill in all fields');
    return;
  }

  if (newPassword !== confirmNewPassword) {
    setError('Passwords do not match');
    return;
  }

  if (newPassword.length < 8) {
    setError('Password must be at least 8 characters');
    return;
  }

  setIsLoading(true);
  
  try {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        token: resetToken,
        password: newPassword 
      })
    });

    const data = await response.json();

    if (response.ok) {
      setResetMessage(data.message);
      // Clear form
      setNewPassword('');
      setConfirmNewPassword('');
      setResetToken('');
      // Redirect to login after 2 seconds
      setTimeout(() => {
        setCurrentView('login');
        setResetMessage('');
      }, 2000);
    } else {
      setError(data.error || 'Failed to reset password');
    }
  } catch (err) {
    setError('Failed to connect to server');
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};

// STREAMING - COMPLETE IMPLEMENTATION

const handleGenerateContent = async () => {
  // ✅ PRESERVED: Original validation
  if (!topic || !contentType) {
    setError('Please fill in all required fields');
    return;
  }
  
  // ✅ PRESERVED: Loading and error state management
  setIsLoading(true);
  setError('');
  setGeneratedContent(''); // Clear previous content for clean streaming
  
  try {
    // 🆕 CHANGED: Use streaming endpoint
    const response = await fetch(`${API_URL}/content/generate-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        contentType,
        topic,
        keywords
      })
    });
    
    // ✅ PRESERVED: Check response status BEFORE reading stream
    if (!response.ok) {
      // For non-streaming errors, parse JSON normally
      const data = await response.json();
      
      // ✅ PRESERVED: Special handling for trial expiration
      if (data.needsSubscription) {
        setError('Your trial has expired. Please subscribe to continue.');
        setCurrentView('upgrade');
      } else {
        setError(data.error || 'Failed to generate content');
      }
      setIsLoading(false);
      return;
    }
    
    // 🆕 NEW: Stream reading logic
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedContent = '';
    let hasStarted = false; // To track if first chunk received

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      // Decode the chunk
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE messages (data: ...\n\n)
      const lines = buffer.split('\n\n');
      
      // Keep incomplete message in buffer
      buffer = lines.pop() || '';
      
      // Process each complete message
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonStr = line.slice(6); // Remove "data: " prefix
            const data = JSON.parse(jsonStr);
            
            // Handle content chunk
            if (data.chunk) {
              accumulatedContent += data.chunk;
              setGeneratedContent(accumulatedContent); // Update UI progressively!

              // 🔥 Switch to result view on FIRST chunk!
              if (!hasStarted) {
                setCurrentView('result');
                hasStarted = true;
              }
            }            

            // Handle completion
            if (data.done) {
              console.log('✅ Streaming complete');
            }
            
            // ✅ PRESERVED: Error handling with trial check
            if (data.error) {
              // Check if error is about trial/subscription
              if (data.error.includes('trial') || 
                  data.error.includes('expired') || 
                  data.error.includes('subscribe')) {
                setError('Your trial has expired. Please subscribe to continue.');
                setCurrentView('upgrade');
              } else {
                setError(data.error);
              }
              setIsLoading(false);
              return;
            }
          } catch (parseError) {
            // Skip invalid JSON (could be incomplete chunk)
            console.warn('Failed to parse chunk:', line);
            continue;
          }
        }
      }
    }
    
  } catch (err) {
    // ✅ PRESERVED: Original error handling
    setError('Failed to generate content. Please try again.');
    console.error(err);
  } finally {
    // ✅ PRESERVED: Cleanup
    setIsLoading(false);
  }
};


  // Convert content to different formats
  const convertToPlainText = (content) => {
    // Remove markdown formatting
    let plain = content
      // Remove headers
      .replace(/#{1,6}\s+/g, '')
      // Remove bold/italic
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      // Remove list markers
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      // Clean up extra whitespace
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    return plain;
  };

  const convertToHTML = (content) => {
    // Basic markdown to HTML conversion
    let html = content
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Links
      .replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2">$1</a>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      // Unordered lists
      .replace(/^\s*[-*+]\s+(.*)$/gm, '<li>$1</li>')
      // Ordered lists
      .replace(/^\s*\d+\.\s+(.*)$/gm, '<li>$1</li>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Wrap in paragraphs if not already wrapped
    if (!html.startsWith('<')) {
      html = '<p>' + html + '</p>';
    }
    
    // Clean up list items
    html = html.replace(/(<li>.*<\/li>)/gm, '<ul>$1</ul>');
    html = html.replace(/<\/ul>\s*<ul>/g, '');
    
    return html;
  };

  const handleCopy = (format = copyFormat) => {
  let textToCopy = generatedContent;
  
  // Convert based on selected format
    switch(format) {
      case 'plain':
        textToCopy = convertToPlainText(generatedContent);
        break;
      case 'html':
        textToCopy = convertToHTML(generatedContent);
        break;
      case 'markdown':
      default:
        textToCopy = generatedContent; // Already in markdown
        break;
    }
    
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setShowFormatMenu(false); // Close menu after copying
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setSubscriptionStatus(null);
    setCurrentView('login');
  };

  const getTrialDays = () => {
    if (subscriptionStatus) {
      return subscriptionStatus.trialDaysRemaining || 0;
    }
    if (!user?.subscription) return 0;
    const trialEnd = new Date(user.subscription.trialEndDate);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysLeft);
  };

  const isTrialActive = () => {
    if (subscriptionStatus) {
      return subscriptionStatus.isTrialActive && getTrialDays() > 0;
    }
    return user?.subscription?.isTrialActive && getTrialDays() > 0;
  };

  const hasActiveAccess = () => {
    if (subscriptionStatus) {
      return subscriptionStatus.hasActiveSubscription || isTrialActive();
    }
    return user?.subscription?.status === 'active' || isTrialActive();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getContentTypeDisplay = (type) => {
    const typeMap = {
      'blog': 'Blog Post',
      'x_threads': 'Social Media Post',
      'linkedin': 'LinkedIn Post'
    };
    return typeMap[type] || type;
  };

  const resetForm = () => {
    setContentType('');
    setTopic('');
    setKeywords('');
    setGeneratedContent('');
    setError('');
    setCopied(false);
    setCurrentView('dashboard');
  };

  // Navigation Component
  const Navigation = () => {
    return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center"> 
        <img 
          src="/images/postmakerlogo.png" 
          alt="PostMaker AI" 
          className="h-8 w-auto sm:h-10 md:h-12 object-contain cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setCurrentView('dashboard')}
        />
        <div className="flex items-center gap-4">
          {currentView !== 'login' && currentView !== 'signup' && (
            <>
              {/* Create Button - New Design with + icon */}
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create
              </button>
            </>
          )}
         {/* Email Verification / Trial Status - In Header */}
          {user && !isLoadingSubscription && !subscriptionStatus?.hasActiveSubscription && (
            <div className={`border rounded-lg px-2 py-1.5 sm:px-4 sm:py-2.5 flex items-center justify-between gap-2 ${
              !user.emailVerified ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'
            }`}>
              {!user.emailVerified ? (
                // Email Not Verified
                <>
                  <p className="text-xs sm:text-sm font-medium text-blue-800">
                    <span className="sm:hidden">Verify email</span>
                    <span className="hidden sm:inline">Please verify your email</span>
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/auth/resend-verification`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ email: user.email })
                        });
                        if (response.ok) {
                          alert('Verification email sent! Check your inbox.');
                        } else {
                          alert('Failed to send email. Please try again.');
                        }
                      } catch (error) {
                        console.error('Resend error:', error);
                        alert('An error occurred. Please try again.');
                      }
                    }}
                    className="bg-blue-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-blue-700"
                  >
                    Resend
                  </button>
                </>
              ) : isTrialActive() ? (
                // Trial Active
                <>
                  <p className="text-xs sm:text-sm font-medium text-yellow-800">
                    <span className="sm:hidden">{getTrialDays()}d left</span>
                    <span className="hidden sm:inline">Trial: {getTrialDays()} days left</span>
                  </p>
                  <button
                    onClick={() => setCurrentView('upgrade')}
                    className="bg-yellow-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-yellow-700"
                  >
                    Upgrade
                  </button>
                </>
              ) : (
                // Trial Expired
                <>
                  <p className="text-xs sm:text-sm font-medium text-yellow-800">
                    <span className="sm:hidden">Trial ended</span>
                    <span className="hidden sm:inline">Trial has ended</span>
                  </p>
                  <button
                    onClick={() => setCurrentView('upgrade')}
                    className="bg-yellow-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-yellow-700"
                  >
                    Upgrade
                  </button>
                </>
              )}
            </div>
          )}
          {user && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-indigo-600 transition"
              >
                <span>{user?.firstName} {user?.lastName}</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      setCurrentView('history');
                      fetchContentHistory();
                      fetchContentStats();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <History size={16} />
                    History
                  </button>
                  <button
                    onClick={() => {
                      setCurrentView('settings');
                      fetchSubscriptionStatus(token);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                  
                  {/* Pro Badge - Moved to dropdown, appears between Settings and Logout */}
                  {subscriptionStatus?.hasActiveSubscription && (
                    <div className="px-4 py-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold rounded-full">
                        <CheckCircle size={14} />
                        Pro
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};  

// KEY CHANGES MADE:
// 1. Removed Pro badge from header navigation (lines 849-854 in original)
// 2. Changed Create button from text link to styled button with + icon
// 3. Added Pro badge inside dropdown menu between Settings and Logout
// 4. Pro badge uses same green color scheme with checkmark icon

  // Admin Dashboard View
  if (currentView === 'admin') {
    return <AdminDashboard token={token} onLogout={handleLogout} />;
  }

  // Forgot Password View
    if (currentView === 'forgot-password') {
    return (
      <ForgotPasswordForm
        onBack={() => setCurrentView('login')}
      />
    );
  }

  // Reset Password View
  if (currentView === 'reset-password') {
    return (
      <ResetPasswordForm
        resetToken={resetToken}  // ← Pass the token from App.js state
        onSuccess={() => setCurrentView('login')}
        onBack={() => setCurrentView('login')}
      />
    );
  }

  // Email verification prompt view
    if (currentView === 'verify-email-prompt') {
    return (
      <VerifyEmailPrompt />
    );
  }


  // Content History View
  if (currentView === 'history') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Content History</h2>
            <p className="text-gray-600">View and manage all your generated content</p>
          </div>

          {/* Stats Cards */}
          {historyStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Content</p>
                    <p className="text-2xl font-bold text-gray-900">{historyStats.totalContent}</p>
                  </div>
                  <FileText className="text-indigo-600" size={32} />
                </div>
              </div>
              {historyStats.byType.map(stat => (
                <div key={stat.type} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <p className="text-sm text-gray-600">{getContentTypeDisplay(stat.type)}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.totalWords.toLocaleString()} words</p>
                </div>
              ))}
            </div>
          )}

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6">
            {['all', 'blog', 'x_threads', 'linkedin'].map(filter => (
              <button
                key={filter}
                onClick={() => setHistoryFilter(filter)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  historyFilter === filter
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {filter === 'all' ? 'All' : getContentTypeDisplay(filter)}
              </button>
            ))}
          </div>

          {/* Content List */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="animate-spin text-indigo-600" size={32} />
            </div>
          ) : contentHistory.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center">
              <History className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No content yet</h3>
              <p className="text-gray-600 mb-4">Start creating content to see it here</p>
              <button
                onClick={resetForm}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
              >
                Create Content
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {contentHistory.map(content => (
                <div key={content.id} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:border-indigo-200 transition">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                          {getContentTypeDisplay(content.contentType)}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar size={14} />
                          {formatDate(content.createdAt)}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{content.topic}</h3>
                      {content.keywords && (
                        <p className="text-sm text-gray-600 mb-2">
                          Keywords: {content.keywords}
                        </p>
                      )}
                      <p className="text-sm text-gray-500">
                        {content.wordCount?.toLocaleString()} words
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => viewContentDetail(content.id)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        title="View content"
                      >
                        <Eye size={20} />
                      </button>
                      <button
                        onClick={() => deleteContent(content.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete content"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Settings/Subscription Management View
  if (currentView === 'settings') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Settings</h2>
            <p className="text-gray-600">Manage your account and subscription</p>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name</label>
                <p className="text-gray-900 font-medium">{user?.firstName} {user?.lastName}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Email</label>
                <p className="text-gray-900 font-medium">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Subscription</h3>
              {subscriptionStatus?.hasActiveSubscription && (
                <span className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full border border-green-200">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-sm text-green-800 font-medium">Active</span>
                </span>
              )}
              {isTrialActive() && (
                <span className="flex items-center gap-2 bg-yellow-50 px-3 py-1 rounded-full border border-yellow-200">
                  <CalendarIcon size={16} className="text-yellow-600" />
                  <span className="text-sm text-yellow-800 font-medium">Trial</span>
                </span>
              )}
              {!subscriptionStatus?.hasActiveSubscription && !isTrialActive() && (
                <span className="flex items-center gap-2 bg-red-50 px-3 py-1 rounded-full border border-red-200">
                  <AlertCircle size={16} className="text-red-600" />
                  <span className="text-sm text-red-800 font-medium">Expired</span>
                </span>
              )}
            </div>

            <div className="space-y-4">
              {/* Trial Info */}
              {isTrialActive() && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1">Free Trial Active</p>
                      <p className="text-sm text-gray-700">
                        You have <strong>{getTrialDays()} days</strong> remaining in your trial.
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        Trial ends: {subscriptionStatus?.trialEndDate ? formatDate(subscriptionStatus.trialEndDate) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCurrentView('upgrade')}
                    className="mt-4 w-full bg-yellow-600 text-white py-2 rounded-lg hover:bg-yellow-700 font-medium"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              )}

              {/* Active Subscription Info */}
              {subscriptionStatus?.hasActiveSubscription && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CreditCardIcon className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1">Pro Subscription</p>
                      <p className="text-sm text-gray-700 mb-2">
                        You have unlimited access to all features.
                      </p>
                      {subscriptionStatus?.daysUntilRenewal !== null && (
                        <p className="text-xs text-gray-600">
                          Next billing date: {subscriptionStatus?.stripeCurrentPeriodEnd ? formatDate(subscriptionStatus.stripeCurrentPeriodEnd) : 'N/A'}
                          {' '}({subscriptionStatus.daysUntilRenewal} days)
                        </p>
                      )}
                    </div>
                  </div>
                  {subscriptionStatus?.canManageBilling && (
                    <button
                      onClick={handleManageBilling}
                      disabled={isLoading}
                      className="mt-4 w-full bg-white border-2 border-green-600 text-green-700 py-2 rounded-lg hover:bg-green-50 font-medium flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={18} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <CreditCardIcon size={18} />
                          Manage Billing
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Expired Subscription */}
              {!subscriptionStatus?.hasActiveSubscription && !isTrialActive() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1">Subscription Expired</p>
                      <p className="text-sm text-gray-700 mb-2">
                        Your trial has ended. Subscribe to continue creating content.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCurrentView('upgrade')}
                    className="mt-4 w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-medium"
                  >
                    Subscribe Now
                  </button>
                </div>
              )}

              {/* Billing Portal Info */}
              {subscriptionStatus?.canManageBilling && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Manage your subscription in the Stripe Customer Portal:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• Update payment method</li>
                    <li>• View billing history and invoices</li>
                    <li>• Cancel subscription</li>
                    <li>• Update billing information</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          {historyStats && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Content</p>
                  <p className="text-2xl font-bold text-gray-900">{historyStats.totalContent}</p>
                </div>
                {historyStats.byType.map(stat => (
                  <div key={stat.type}>
                    <p className="text-sm text-gray-600">{getContentTypeDisplay(stat.type)}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Content Detail View
  if (currentView === 'content-detail' && selectedContent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <div className="max-w-4xl mx-auto px-4 py-12">
          <button
            onClick={() => setCurrentView('history')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            ← Back to History
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                    {getContentTypeDisplay(selectedContent.contentType)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(selectedContent.createdAt)}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedContent.topic}</h2>
                {selectedContent.keywords && (
                  <p className="text-sm text-gray-600 mb-2">
                    Keywords: {selectedContent.keywords}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  {selectedContent.wordCount?.toLocaleString()} words
                </p>
              </div>
              <button
                onClick={() => handleCopy(selectedContent.generatedContent)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                {copied ? (
                  <>
                    <CheckCircle size={18} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    Copy
                  </>
                )}
              </button>
            </div>
            
            <div className="prose max-w-none bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(selectedContent.generatedContent)
                }} />
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setContentType(getContentTypeDisplay(selectedContent.contentType));
                setTopic(selectedContent.topic);
                setKeywords(selectedContent.keywords || '');
                setCurrentView('dashboard');
              }}
              className="flex-1 bg-indigo-50 text-indigo-700 py-3 rounded-lg font-semibold hover:bg-indigo-100 transition border border-indigo-200"
            >
              Create Similar Content
            </button>
            <button
              onClick={() => deleteContent(selectedContent.id)}
              className="px-6 bg-red-50 text-red-700 py-3 rounded-lg font-semibold hover:bg-red-100 transition border border-red-200"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upgrade/Subscription View
  if (currentView === 'upgrade') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-indigo-600" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Upgrade to Pro</h1>
            <p className="text-gray-600">Continue creating amazing content!</p>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border-2 border-indigo-200">
            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-indigo-600">$14</div>
              <div className="text-gray-600">per month</div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Unlimited AI-generated content</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">All content types (Blog, X, LinkedIn)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">SEO-optimized content</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Content history & analytics</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Priority support</span>
              </div>
            </div>

            <button
              onClick={handleCreateCheckout}
              disabled={isLoading}
              className="w-full bg-indigo-600 text-white py-4 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Loading...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Subscribe Now
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm mb-4">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={() => setCurrentView('dashboard')}
            className="w-full text-gray-600 hover:text-gray-900 text-sm"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'login') {
    return (
      <LoginForm
        onSwitchToSignup={() => setCurrentView('signup')}
        onSwitchToForgotPassword={() => setCurrentView('forgot-password')}
        onSuccess={(userData, authToken) => {
          setUser(userData);
          setToken(authToken);
          
          // Set subscription status immediately
          if (userData.subscription) {
            const calculateTrialDays = (trialEndDate) => {
              if (!trialEndDate) return 0;
              const end = new Date(trialEndDate);
              const now = new Date();
              const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
              return Math.max(0, daysLeft);
            };
            
            const subStatus = {
              status: userData.subscription.status,
              isTrialActive: userData.subscription.isTrialActive,
              trialDaysRemaining: calculateTrialDays(userData.subscription.trialEndDate),
              trialEndDate: userData.subscription.trialEndDate,
              stripeCurrentPeriodEnd: userData.subscription.stripeCurrentPeriodEnd,
              hasActiveSubscription: userData.subscription.status === 'active'
            };
            setSubscriptionStatus(subStatus);
            setIsLoadingSubscription(false);
          }
          
          setCurrentView('dashboard');
        }}
      />
    );
  }

  if (currentView === 'signup') {
  return (
    <SignupForm
      onSwitchToLogin={() => setCurrentView('login')}
      onSuccess={() => setCurrentView('verify-email-prompt')}
    />
  );
}

  if (currentView === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 py-12">
          {!isLoadingSubscription && subscriptionStatus && !hasActiveAccess() && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-1">Trial Expired</h3>
                  <p className="text-red-800 mb-3">Your free trial has ended. Subscribe to continue creating content.</p>
                  <button
                    onClick={() => setCurrentView('upgrade')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">What would you like to create today?</h2>
            <p className="text-gray-600 mb-8">Choose your content type and let AI do the heavy lifting</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Content Type *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Blog Post', value: 'blog' },
                    { label: 'Social Media Post', value: 'x_threads' },
                    { label: 'LinkedIn Post', value: 'linkedin' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setContentType(type.value)}
                      className={`p-4 border-2 rounded-lg text-center transition ${
                        contentType === type.value
                          ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <span className="font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What do you want to write about? *
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-24"
                  rows={6}
                  placeholder="Describe your topic in a few sentences. Be specific about your main message or goal..."
                  value={topic}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      setTopic(value);
                      setTopicCharCount(value.length);
                    }
                  }}
                  maxLength={500}
                />
                {/* Character Counter */}
                <div className="flex justify-end mt-2">
                  <span 
                    className={`text-sm font-medium transition-colors ${
                      topicCharCount >= 500 
                        ? 'text-red-600' 
                        : topicCharCount >= 475 
                        ? 'text-red-500' 
                        : topicCharCount >= 450 
                        ? 'text-orange-500' 
                        : 'text-gray-500'
                    }`}
                  >
                    {topicCharCount}/500 characters
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords (optional)
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="keyword1, keyword2, keyword3"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
                <p className="text-sm text-gray-500 mt-1">Separate keywords with commas</p>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              
              <button
                onClick={handleGenerateContent}
                disabled={isLoading || !contentType || !hasActiveAccess()}
                className="w-full bg-indigo-600 text-white py-4 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Generating...
                  </>
                ) : (
                  `Write ${contentType ? getContentTypeDisplay(contentType) : 'Content'}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'result') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Your {getContentTypeDisplay(contentType)}</h2>
                <p className="text-gray-600">AI-optimized and ready to publish</p>
              </div>
              
              {/* Copy Format Dropdown */}
              <div className="relative copy-format-container">
                <div className="flex gap-2">
                  {/* Main Copy Button */}
                  <button
                    onClick={() => handleCopy()}
                    className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    {copied ? (
                      <>
                        <CheckCircle size={18} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={18} />
                        Copy as {copyFormat === 'plain' ? 'Plain Text' : copyFormat === 'html' ? 'HTML' : 'Markdown'}
                      </>
                    )}
                  </button>

                  {/* Format Dropdown Button */}
                  <button
                    onClick={() => setShowFormatMenu(!showFormatMenu)}
                    className="bg-indigo-600 text-white px-3 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors"
                    title="Choose format"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Format Menu Dropdown */}
                {showFormatMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                      Copy Format
                    </div>
                    
                    <button
                      onClick={() => {
                        setCopyFormat('plain');
                        handleCopy('plain');
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                        copyFormat === 'plain' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">📄</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Plain Text</div>
                        <div className="text-xs text-gray-500">No formatting</div>
                      </div>
                      {copyFormat === 'plain' && (
                        <CheckCircle size={16} className="text-indigo-600" />
                      )}
                    </button>

                    <button
                      onClick={() => {
                        setCopyFormat('html');
                        handleCopy('html');
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                        copyFormat === 'html' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">🌐</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm">HTML</div>
                        <div className="text-xs text-gray-500">For websites</div>
                      </div>
                      {copyFormat === 'html' && (
                        <CheckCircle size={16} className="text-indigo-600" />
                      )}
                    </button>

                    <button
                      onClick={() => {
                        setCopyFormat('markdown');
                        handleCopy('markdown');
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                        copyFormat === 'markdown' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">📝</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Markdown</div>
                        <div className="text-xs text-gray-500">For editors</div>
                      </div>
                      {copyFormat === 'markdown' && (
                        <CheckCircle size={16} className="text-indigo-600" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="prose max-w-none bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(generatedContent)
              }}
              />
            </div>
          </div>
          
          <div className="bg-indigo-50 rounded-2xl p-6 border-2 border-indigo-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">What's next?</h3>
            <div className="space-y-3">
              <button
                onClick={resetForm}
                className="w-full bg-white text-indigo-700 py-3 rounded-lg font-semibold hover:bg-indigo-100 transition border border-indigo-200"
              >
                Create Another Post
              </button>
              
              {contentType === 'Blog Post' && (
                <button
                  onClick={() => {
                    setContentType('X/Threads Post');
                    setTopic(`Create a X/Threads post promoting this blog post: ${topic}`);
                    setCurrentView('dashboard');
                  }}
                  className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
                >
                  Create X or Threads Post for This Blog
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
export default function App() {
  return (
    <AuthProvider>
      <ContentCreatorApp />
    </AuthProvider>
  );
}  